import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../apiConfig';
import '../styles/ArtworkUpload.css';
import { FiUploadCloud, FiTrash2, FiImage, FiStar } from 'react-icons/fi';
import { useStripeOnboardingStatus } from './PaymentSettings';
import ArtistSearch from './ArtistSearch';
import { toast } from 'react-toastify';
import { calculateArtworkPricing } from '../utils/pricing';
import { isHeicFile, convertHeicToJpeg, compressImage } from '../utils/imagePrep';

const ArtworkUpload = ({ isModal = false, onClose, initialData = null }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [price, setPrice] = useState(initialData?.price || '');
  const [medium, setMedium] = useState(initialData?.medium || '');
  const [dimensions, setDimensions] = useState({
    width: initialData?.dimensions?.width || '',
    height: initialData?.dimensions?.height || '',
    depth: initialData?.dimensions?.depth || '',
    unit: initialData?.dimensions?.unit || 'inches'
  });
  const [yearCreated, setYearCreated] = useState(initialData?.year_created || new Date().getFullYear());
  const [artistName, setArtistName] = useState(initialData?.artist_name || '');
  const [editionType, setEditionType] = useState(initialData?.edition_type || 'original');
  const [editionSize, setEditionSize] = useState(initialData?.edition_size || '1');
  const [editionNumber, setEditionNumber] = useState(initialData?.edition_number || '1');
  const [images, setImages] = useState([]); // Array of File objects for new uploads
  const [existingImages, setExistingImages] = useState(initialData?.images || []); // Existing images for editing
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);
  const imagesRef = useRef([]);
  const uploadQueue = useRef(Promise.resolve());
  const navigate = useNavigate();

  
  // User profile and artist selection for galleries
  const [userProfile, setUserProfile] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState(null);
  // 'owned': the gallery holds the work, sells it, and is paid. No artist approval.
  // 'artist': listed under an Art Emerge artist's account, which needs their approval.
  const [galleryUploadMode, setGalleryUploadMode] = useState('owned');
  const [profileLoading, setProfileLoading] = useState(true);
  
  // Upload limits state
  const [uploadLimits, setUploadLimits] = useState({
    maxFileSizeMB: 10,
    maxFilesPerUpload: 10,
    maxArtworksPerUser: 100,
    allowedTypesDisplay: 'JPEG, PNG, WebP, GIF, HEIC',
    currentArtworkCount: 0,
    remainingArtworks: 100,
    canUpload: true
  });

  const pricingPreview = useMemo(() => {
    return calculateArtworkPricing(price, dimensions);
  }, [price, dimensions]);
  const showLowPriceGuidance = pricingPreview && pricingPreview.artistBasePrice < 200;

  useEffect(() => {
    if (!isModal) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isModal]);

  // Fetch user profile and upload limits
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await api.get(endpoints.user.profile);
        setUserProfile(response.data);
        console.log('User profile loaded:', response.data);
        
        // Set default artist name if user is an artist and no initial data
        if (!initialData?.artist_name && response.data?.role === 'artist' && response.data?.name) {
          setArtistName(response.data.name);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError('Failed to load user profile');
      } finally {
        setProfileLoading(false);
      }
    };

    const fetchUploadLimits = async () => {
      try {
        const response = await api.get(endpoints.artwork.uploadLimits);
        setUploadLimits(response.data);
        console.log('Upload limits loaded:', response.data);
        
        // Show warning if approaching limit
        if (response.data.remainingArtworks <= 5 && response.data.remainingArtworks > 0) {
          toast.warning(`You have ${response.data.remainingArtworks} artwork upload${response.data.remainingArtworks === 1 ? '' : 's'} remaining.`, { autoClose: 6000 });
        } else if (!response.data.canUpload) {
          toast.error(`You have reached the maximum of ${response.data.maxArtworksPerUser} artworks. Please delete some to upload more.`, { autoClose: 8000 });
        }
      } catch (error) {
        console.error('Error fetching upload limits:', error);
        // Use defaults - don't block the user
      }
    };

    fetchUserProfile();
    if (!initialData) {
      fetchUploadLimits(); // Only fetch limits for new uploads
    }
  }, [initialData]);

  const rememberImages = (nextOrUpdater) => {
    setImages((prev) => {
      const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
      imagesRef.current = next;
      return next;
    });
  };

  // Let the browser set the multipart boundary. A bare
  // "multipart/form-data" header makes the server see no file.
  const postArtworkImages = async (files, primaryIndex = 0) => {
    const imageData = new FormData();
    files.forEach((image) => {
      imageData.append('images', image);
    });

    const imageResponse = await api.post(`/artworks/${initialData.id}/images`, imageData, {
      transformRequest: [(data, headers) => {
        if (headers?.delete) headers.delete('Content-Type');
        else if (headers) delete headers['Content-Type'];
        return data;
      }]
    });

    const uploaded = imageResponse.data?.images || [];
    if (!uploaded.length) {
      throw new Error(imageResponse.data?.error || 'The image did not upload. Please try again.');
    }

    const chosen = uploaded[primaryIndex] || uploaded[0];
    if (chosen?.id) {
      await api.put(`/artworks/${initialData.id}/images/${chosen.id}/primary`);
    }

    setExistingImages((prev) => {
      const kept = prev.map((img) => ({ ...img, is_primary: false }));
      const added = uploaded.map((img) => ({
        ...img,
        is_primary: img.id === chosen?.id
      }));
      const primary = added.find((img) => img.is_primary) || added[0];
      const others = added.filter((img) => img !== primary);
      return [primary, ...others, ...kept].filter(Boolean);
    });

    return chosen;
  };

  const enqueueImageUpload = (files, primaryIndex = 0) => {
    const run = uploadQueue.current.then(() => postArtworkImages(files, primaryIndex));
    uploadQueue.current = run.then(() => undefined, () => undefined);
    return run;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const uploadingForArtist = !initialData
        && userProfile?.role === 'gallery'
        && galleryUploadMode === 'artist';

      if (uploadingForArtist && !selectedArtist) {
        const msg = 'Choose the artist account, or switch to “Our gallery owns this work”';
        setError(msg);
        toast.error(msg);
        setLoading(false);
        return;
      }

      // Validate required fields before submission
      if (!title.trim()) {
        const msg = 'Please enter a title for your artwork';
        setError(msg);
        toast.error(msg);
        setLoading(false);
        return;
      }

      if (!initialData && imagesRef.current.length === 0) {
        const msg = 'Please upload at least one image of your artwork';
        setError(msg);
        toast.error(msg);
        setLoading(false);
        return;
      }

      // For new uploads we send multipart/form-data with images.
      // For edits we send JSON to the update endpoint (images handled by separate routes).
      let response;
      let savedNewImage = false;
      if (!initialData) {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('price', price);
        formData.append('medium', medium);
        formData.append('dimensions', JSON.stringify(dimensions));
        formData.append('year_created', yearCreated);
        formData.append('artist_name', artistName);
        formData.append('edition_type', editionType);
        formData.append('edition_size', editionSize);
        formData.append('edition_number', editionNumber);

        if (uploadingForArtist && selectedArtist) {
          formData.append('artist_id', selectedArtist.id);
          formData.set('artist_name', selectedArtist.name);
        }

        // Add all images to FormData
        imagesRef.current.forEach((image) => {
          formData.append('images', image);
        });

        response = await api({
          method: 'POST',
          url: endpoints.artwork.upload,
          data: formData,
          transformRequest: [(data, headers) => {
            if (headers?.delete) headers.delete('Content-Type');
            else if (headers) delete headers['Content-Type'];
            return data;
          }],
          validateStatus: () => true // handle non-2xx ourselves for better messages
        });

        if (response.status >= 400) {
          // Extract detailed error information from response
          const errorData = response.data || {};
          const errorCode = errorData.code || 'UNKNOWN_ERROR';
          const errorMsg = errorData.error || `Upload failed (${response.status})`;
          const hint = errorData.hint;
          const filename = errorData.filename;
          
          // Show appropriate toast based on error type
          let toastMessage = errorMsg;
          
          switch (response.status) {
            case 413: // Payload Too Large
              toastMessage = filename 
                ? `Image "${filename}" is too large. Maximum size is 10MB per image.`
                : 'One or more images are too large. Maximum size is 10MB per image.';
              toast.error(toastMessage, { autoClose: 8000 });
              if (hint) toast.info(hint, { autoClose: 10000 });
              break;
            case 415: // Unsupported Media Type
              toastMessage = filename
                ? `File "${filename}" is not a supported image type.`
                : 'Invalid file type. Please upload JPEG, PNG, WebP, GIF, or HEIC images only.';
              toast.error(toastMessage, { autoClose: 6000 });
              break;
            case 403: // Forbidden (artwork limit reached or representation required)
              if (errorCode === 'ARTWORK_LIMIT_REACHED') {
                toast.error(errorMsg, { autoClose: 10000 });
                if (hint) toast.info(hint, { autoClose: 8000 });
              } else {
                toast.error(errorMsg || 'You do not have permission to upload artwork.', { autoClose: 8000 });
              }
              break;
            case 409:
              toast.error(errorMsg, { autoClose: 10000 });
              break;
            case 503: // Service Unavailable (Cloudinary issues)
              toast.error('Image upload service is temporarily unavailable. Please try again in a few minutes.', { autoClose: 8000 });
              break;
            case 400: // Bad Request
              toast.error(toastMessage, { autoClose: 6000 });
              break;
            default:
              toast.error('Failed to upload artwork. Please try again.', { autoClose: 5000 });
          }
          
          throw new Error(errorMsg);
        }
      } else {
        // PUT JSON payload for edits
        const payload = {
          title,
          description,
          medium,
          dimensions, // backend accepts object or JSON string
          year_created: yearCreated,
          price: price === '' ? null : parseFloat(price),
          tags: Array.isArray(initialData?.tags) ? initialData.tags : [],
          edition_type: editionType,
          edition_size: editionSize ? parseInt(editionSize) : null,
          edition_number: editionNumber ? parseInt(editionNumber) : null
        };

        response = await api.put(endpoints.artwork.update(initialData.id), payload);

        // A drop uploads immediately. This covers a file that is still
        // waiting because that upload failed, and a save click that races it.
        await uploadQueue.current;
        const pendingImages = imagesRef.current;
        if (pendingImages.length > 0) {
          const boundedIndex = Math.min(primaryImageIndex, pendingImages.length - 1);
          await enqueueImageUpload(pendingImages, Math.max(0, boundedIndex));
          rememberImages([]);
          savedNewImage = true;
        }
      }

      if (isModal && onClose) {
        onClose(response.data);
      } else {
        toast.success(
          initialData
            ? (savedNewImage ? 'Artwork updated. The new image is now the primary image.' : 'Artwork updated')
            : 'Artwork uploaded successfully! 🎨',
          { autoClose: 4000 }
        );
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.details
        || err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || (initialData ? 'Failed to update artwork' : 'Failed to upload artwork');
      setError(errorMessage);
      if (initialData) {
        toast.error(errorMessage, { autoClose: 6000 });
      }
      // Only show generic toast if we haven't already shown a specific one
      if (!err.message?.includes('too large') && !err.message?.includes('file type') && !err.message?.includes('limit')) {
        // Error already shown via specific toast above
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    await addFiles(files);
  };

  const addFiles = async (files) => {
    if (!files || files.length === 0) return;

    // The artwork cap applies to new listings. Replacing an image on an
    // existing listing is not a new artwork.
    if (!initialData && !uploadLimits.canUpload) {
      toast.error(`You have reached the maximum of ${uploadLimits.maxArtworksPerUser} artworks. Please delete some to upload more.`, { autoClose: 6000 });
      return;
    }

    const validFiles = [];
    const maxFiles = uploadLimits.maxFilesPerUpload;
    const sizeLimit = uploadLimits.maxFileSizeMB * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    const rejectedFiles = [];

    for (const original of files) {
      // Check if we've reached max files
      if (imagesRef.current.length + validFiles.length >= maxFiles) {
        toast.warning(`Maximum ${maxFiles} images allowed per artwork. Some files were not added.`, { autoClose: 5000 });
        break;
      }

      // Convert HEIC/HEIF to JPEG when possible
      let file = isHeicFile(original) ? await convertHeicToJpeg(original) : original;
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        rejectedFiles.push({ name: file.name, reason: 'not an image' });
        continue;
      }

      // Check if type is in our allowed list (after potential HEIC conversion)
      const isAllowedType = allowedTypes.some(type => file.type === type || file.type.startsWith('image/'));
      if (!isAllowedType) {
        rejectedFiles.push({ name: file.name, reason: `unsupported format (${file.type})` });
        continue;
      }

      // Attempt compression if file is large or likely high-res
      try {
        const needsCompression = file.size > sizeLimit * 0.8; // compress if close to limit
        if (needsCompression) {
          const beforeMb = (file.size / (1024 * 1024)).toFixed(2);
          toast.info(`Compressing ${file.name}...`, { autoClose: 2000 });
          
          const compressed = await compressImage(file, { maxBytes: sizeLimit * 0.95 });
          if (compressed.size >= file.size) {
            // If compression ineffective, keep original (will fail later if over limit)
            if (file.size > sizeLimit) {
              rejectedFiles.push({ 
                name: file.name, 
                reason: `too large (${beforeMb}MB) and could not be compressed - max ${uploadLimits.maxFileSizeMB}MB` 
              });
              continue;
            }
          } else {
            const afterMb = (compressed.size / (1024 * 1024)).toFixed(2);
            toast.success(`Compressed ${file.name}: ${beforeMb}MB → ${afterMb}MB`, { autoClose: 3000 });
            file = compressed;
          }
        }
      } catch (compressionError) {
        console.warn('Compression failed for', file.name, compressionError);
        // If compression fails and file exceeds server limit, reject it
        if (file.size > sizeLimit) {
          const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
          rejectedFiles.push({ 
            name: file.name, 
            reason: `too large (${sizeMb}MB) - max ${uploadLimits.maxFileSizeMB}MB` 
          });
          continue;
        }
      }

      // Final size check
      if (file.size > sizeLimit) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
        rejectedFiles.push({ 
          name: file.name, 
          reason: `too large (${sizeMb}MB) - max ${uploadLimits.maxFileSizeMB}MB` 
        });
        continue;
      }

      validFiles.push(file);
    }

    // Show consolidated error message for rejected files
    if (rejectedFiles.length > 0) {
      if (rejectedFiles.length === 1) {
        const rejected = rejectedFiles[0];
        setError(`"${rejected.name}" could not be added: ${rejected.reason}`);
        toast.error(`"${rejected.name}" could not be added: ${rejected.reason}`, { autoClose: 6000 });
      } else {
        const summary = `${rejectedFiles.length} files could not be added`;
        setError(summary);
        toast.error(summary, { autoClose: 4000 });
        // Show details for first few files
        rejectedFiles.slice(0, 3).forEach(rejected => {
          toast.warning(`"${rejected.name}": ${rejected.reason}`, { autoClose: 5000 });
        });
        if (rejectedFiles.length > 3) {
          toast.info(`...and ${rejectedFiles.length - 3} more files were rejected`, { autoClose: 4000 });
        }
      }
    }

    if (validFiles.length > 0) {
      rememberImages(prev => [...prev, ...validFiles]);
      if (rejectedFiles.length === 0) {
        setError(''); // Only clear error if no rejections
      }

      if (initialData) {
        setImageUploading(true);
        try {
          await enqueueImageUpload(validFiles, 0);
          rememberImages(prev => prev.filter(file => !validFiles.includes(file)));
          toast.success('The listing image is updated.', { autoClose: 4000 });
        } catch (err) {
          const message = err.response?.data?.error
            || err.response?.data?.details
            || err.message
            || 'The image was not saved.';
          setError(message);
          toast.error(`${message} Click Update Artwork to try again.`, { autoClose: 8000 });
        } finally {
          setImageUploading(false);
        }
      } else {
        toast.success(
          `${validFiles.length} image${validFiles.length > 1 ? 's' : ''} added`,
          { autoClose: 4000 }
        );
      }
    }
  };

  const removeImage = (index) => {
    rememberImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      // Adjust primary index if needed
      if (primaryImageIndex >= newImages.length) {
        setPrimaryImageIndex(Math.max(0, newImages.length - 1));
      } else if (primaryImageIndex === index) {
        setPrimaryImageIndex(0);
      } else if (primaryImageIndex > index) {
        setPrimaryImageIndex(primaryImageIndex - 1);
      }
      return newImages;
    });
  };

  const moveImage = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || Number.isNaN(fromIndex)) return;

    rememberImages(prev => {
      const newImages = [...prev];
      const [movedImage] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, movedImage);
      
      // Update primary index
      if (primaryImageIndex === fromIndex) {
        setPrimaryImageIndex(toIndex);
      } else if (fromIndex < primaryImageIndex && toIndex >= primaryImageIndex) {
        setPrimaryImageIndex(primaryImageIndex - 1);
      } else if (fromIndex > primaryImageIndex && toIndex <= primaryImageIndex) {
        setPrimaryImageIndex(primaryImageIndex + 1);
      }
      
      return newImages;
    });
  };

  const setPrimaryImage = (index) => {
    setPrimaryImageIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    await addFiles(files);
  };

  const handleDimensionChange = (field, value) => {
    setDimensions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderImagePreview = (image, index) => {
    const isPrimary = index === primaryImageIndex;
    
    return (
      <div 
        key={index} 
        className={`image-preview ${isPrimary ? 'primary' : ''}`}
        draggable
        onDragStart={(e) => e.dataTransfer.setData('text/plain', index)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.dataTransfer.files?.length) {
            handleDrop(e);
            return;
          }
          const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
          moveImage(fromIndex, index);
        }}
      >
        <img 
          src={URL.createObjectURL(image)} 
          alt={`Preview ${index + 1}`} 
          className="preview-image"
        />
        
        <div className="image-overlay">
          <button
            type="button"
            className={`primary-button ${isPrimary ? 'active' : ''}`}
            onClick={() => setPrimaryImage(index)}
            title={isPrimary ? 'Primary image' : 'Set as primary'}
          >
            <FiStar />
          </button>
          
          <button
            type="button"
            className="delete-button"
            onClick={() => removeImage(index)}
            title="Remove image"
          >
            <FiTrash2 />
          </button>
        </div>
        
        <div className="image-info">
          <span className="image-number">{index + 1}</span>
          {isPrimary && <span className="primary-badge">Primary</span>}
        </div>
      </div>
    );
  };

  const setExistingPrimary = async (image) => {
    if (!initialData || !image?.id || image.is_primary || imageUploading) return;
    setImageUploading(true);
    try {
      await api.put(`/artworks/${initialData.id}/images/${image.id}/primary`);
      setExistingImages((prev) => prev.map((img) => ({
        ...img,
        is_primary: img.id === image.id
      })));
      toast.success('Primary image updated.', { autoClose: 4000 });
    } catch (err) {
      const message = err.response?.data?.error || 'Could not change the primary image.';
      toast.error(message, { autoClose: 6000 });
    } finally {
      setImageUploading(false);
    }
  };

  const removeExistingImage = async (image) => {
    if (!initialData || !image?.id || imageUploading) return;
    if (existingImages.length <= 1) {
      toast.error('An artwork needs at least one image.');
      return;
    }
    if (!window.confirm('Remove this image from the artwork?')) return;

    setImageUploading(true);
    try {
      const response = await api.delete(`/artworks/${initialData.id}/images/${image.id}`);
      const remaining = response.data?.images;
      setExistingImages(Array.isArray(remaining)
        ? remaining
        : existingImages.filter((img) => img.id !== image.id));
      toast.success('Image removed.', { autoClose: 4000 });
    } catch (err) {
      const message = err.response?.data?.details
        || err.response?.data?.error
        || 'Could not remove the image.';
      toast.error(message, { autoClose: 6000 });
    } finally {
      setImageUploading(false);
    }
  };

  const renderExistingImages = () => {
    if (!initialData || !existingImages.length) return null;
    
    return (
      <div
        className={`existing-images ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <h3>Current Images</h3>
        <div className="images-grid">
          {existingImages.map((image) => (
            <div
              key={image.id}
              className={`existing-image ${image.is_primary ? 'primary' : ''}`}
            >
              <img src={image.image_url} alt={image.alt_text || 'Artwork image'} className="preview-image" />
              <div className="existing-image-actions">
                {image.is_primary ? (
                  <span className="primary-badge">Primary</span>
                ) : (
                  <button
                    type="button"
                    className="make-primary-button"
                    onClick={() => setExistingPrimary(image)}
                    disabled={imageUploading}
                  >
                    Make primary
                  </button>
                )}
                {existingImages.length > 1 && (
                  <button
                    type="button"
                    className="remove-image-button"
                    onClick={() => removeExistingImage(image)}
                    disabled={imageUploading}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="existing-images-note">
          The image marked Primary is the one on the listing. Make primary switches it. Remove deletes an image you no longer want. Both save immediately.
        </p>
      </div>
    );
  };

  const renderUploadArea = () => {
    const canUploadMore = images.length < uploadLimits.maxFilesPerUpload && uploadLimits.canUpload;
    
    return (
      <div 
        className={`upload-area ${isDragging ? 'dragging' : ''} ${images.length > 0 ? 'has-files' : ''} ${!uploadLimits.canUpload ? 'disabled' : ''}`}
        onDragOver={canUploadMore ? handleDragOver : undefined}
        onDragLeave={canUploadMore ? handleDragLeave : undefined}
        onDrop={canUploadMore ? handleDrop : undefined}
        onClick={canUploadMore ? () => fileInputRef.current?.click() : undefined}
        style={!canUploadMore ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.heic,.heif,.jpg,.jpeg,.png,.gif,.webp"
          onChange={handleFileSelect}
          className="file-input"
          multiple
          required={!initialData && images.length === 0}
          disabled={!canUploadMore}
        />
        
        {!uploadLimits.canUpload ? (
          <div className="upload-prompt">
            <FiImage size={48} className="upload-icon" style={{ color: '#dc2626' }} />
            <p style={{ color: '#dc2626', fontWeight: 'bold' }}>
              Artwork Limit Reached
            </p>
            <p className="file-info">
              You have reached the maximum of {uploadLimits.maxArtworksPerUser} artworks.<br/>
              Please delete some existing artworks from your dashboard to upload new ones.
            </p>
          </div>
        ) : images.length > 0 ? (
          <div className="images-preview">
            <div className="images-grid">
              {images.map((image, index) => renderImagePreview(image, index))}
            </div>
            <div className="upload-more">
              <FiUploadCloud size={24} />
              <span>Add More Images ({images.length}/{uploadLimits.maxFilesPerUpload})</span>
            </div>
          </div>
        ) : (
          <div className="upload-prompt">
            <FiUploadCloud size={48} className="upload-icon" />
            <p>
              {initialData
                ? <>Drop a new image here, or <span className="browse-text">browse</span>. It replaces the listing image as soon as it finishes uploading.</>
                : <>Drag and drop your images here, or <span className="browse-text">browse</span></>}
            </p>
            <p className="file-info">
              Upload multiple images to show different angles or details<br/>
              Max {uploadLimits.maxFilesPerUpload} images, {uploadLimits.maxFileSizeMB}MB each • Supported: {uploadLimits.allowedTypesDisplay}
            </p>
            {uploadLimits.remainingArtworks <= 10 && uploadLimits.remainingArtworks > 0 && (
              <p className="file-info" style={{ color: '#f59e0b', marginTop: '0.5rem' }}>
                ⚠️ You can upload {uploadLimits.remainingArtworks} more artwork{uploadLimits.remainingArtworks === 1 ? '' : 's'}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderForm = () => {
    const totalImages = images.length + existingImages.length;
    
    return (
      <form onSubmit={handleSubmit} className="artwork-form">
        <div className="form-group">
          <label>Images {totalImages > 0 && `(${totalImages})`}</label>
          {renderUploadArea()}
          {renderExistingImages()}
          
          {images.length > 1 && (
            <div className="images-help">
              <p>💡 <strong>Tip:</strong> Drag images to reorder them. The first image will be used as the primary image in listings.</p>
            </div>
          )}
        </div>

        {userProfile?.role === 'gallery' && !initialData && (
          <fieldset className="form-group gallery-ownership">
            <legend>Who owns this artwork?</legend>
            <label className={`ownership-option ${galleryUploadMode === 'owned' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="gallery-upload-mode"
                value="owned"
                checked={galleryUploadMode === 'owned'}
                onChange={() => {
                  setGalleryUploadMode('owned');
                  setSelectedArtist(null);
                }}
              />
              <span>
                <strong>Our gallery owns this work</strong>
                <small>Work you have on hand. It is listed under your gallery, you publish it, and the sale pays your gallery. No artist approval needed.</small>
              </span>
            </label>
            <label className={`ownership-option ${galleryUploadMode === 'artist' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="gallery-upload-mode"
                value="artist"
                checked={galleryUploadMode === 'artist'}
                onChange={() => setGalleryUploadMode('artist')}
              />
              <span>
                <strong>List it for an artist on Art Emerge</strong>
                <small>The artist keeps the listing and is paid. They have to approve your gallery first, and they publish it.</small>
              </span>
            </label>
          </fieldset>
        )}

        {userProfile?.role === 'gallery' && !initialData && galleryUploadMode === 'artist' ? (
          <div className="form-group">
            <label>Artist account <span className="required">*</span></label>
            <ArtistSearch
              selectedArtist={selectedArtist}
              onSelect={(artist) => {
                setSelectedArtist(artist);
                if (artist) setArtistName(artist.name);
              }}
              placeholder="Search for the artist's Art Emerge profile..."
              required={false}
            />
            <small className="form-help">
              The upload is saved under their account and stays unpublished until they publish it. If this title is already on Art Emerge, add that listing to an exhibition instead.
            </small>
          </div>
        ) : (
          <div className="form-group">
            <label>Artist Name <span className="required">*</span></label>
            <input
              type="text"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              placeholder="Enter the artist's name"
              required
            />
            <small className="form-help">
              {userProfile?.role === 'artist'
                ? 'This defaults to your name, but you can change it if selling another artist\'s work.'
                : userProfile?.role === 'gallery'
                  ? 'The name shown on the listing. The artist does not need an Art Emerge account.'
                  : 'Enter the name of the artist who created this artwork.'}
            </small>
          </div>
        )}

        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="4"
            placeholder="Describe your artwork, its inspiration, techniques used, or any story behind it..."
          />
        </div>

        <div className="form-group">
          <label>Medium</label>
          <select
            value={medium}
            onChange={(e) => setMedium(e.target.value)}
            required
          >
            <option value="">Select a medium</option>
            <option value="oil">Oil</option>
            <option value="acrylic">Acrylic</option>
            <option value="watercolor">Watercolor</option>
            <option value="mixed_media">Mixed Media</option>
            <option value="digital">Digital</option>
            <option value="charcoal">Charcoal</option>
            <option value="ink">Ink</option>
            <option value="pastel">Pastel</option>
            <option value="gouache">Gouache</option>
            <option value="tempera">Tempera</option>
            <option value="encaustic">Encaustic</option>
            <option value="collage">Collage</option>
            <option value="graphite">Graphite</option>
            <option value="casein">Casein</option>
            <option value="alkyd">Alkyd</option>
            <option value="airbrush">Airbrush</option>
            <option value="dye">Dye</option>
            <option value="photography">Photography</option>
            <option value="sculpture">Sculpture</option>
          </select>        </div>

        <div className="form-group dimensions">
          <label>Dimensions</label>
          <div className="dimensions-inputs">
            <div className="dimension-field">
              <input
                type="number"
                value={dimensions.width}
                onChange={(e) => handleDimensionChange('width', e.target.value)}
                placeholder="Width"
                step="0.1"
                min="0"
                required
              />
              <span>W</span>
            </div>
            <div className="dimension-field">
              <input
                type="number"
                value={dimensions.height}
                onChange={(e) => handleDimensionChange('height', e.target.value)}
                placeholder="Height"
                step="0.1"
                min="0"
                required
              />
              <span>H</span>
            </div>
            <div className="dimension-field">
              <input
                type="number"
                value={dimensions.depth}
                onChange={(e) => handleDimensionChange('depth', e.target.value)}
                placeholder="Depth"
                step="0.1"
                min="0"
              />
              <span>D</span>
            </div>
            <select
              value={dimensions.unit}
              onChange={(e) => handleDimensionChange('unit', e.target.value)}
            >
              <option value="inches">inches</option>
              <option value="cm">cm</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>
            Price (USD)
            <span className="form-optional"> Recommended minimum: $200</span>
          </label>
          <div className="price-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={price}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
                  setPrice(value);
                }
              }}
              required
              placeholder="0.00"
            />
          </div>
          <small className="form-help">
            Enter the amount you want to receive as the artwork price before Art Emerge&apos;s seller fee. We recommend pricing original artwork at $200+ so shipping and platform fees do not outweigh your earnings.
          </small>
          {showLowPriceGuidance && (
            <div className="pricing-warning" role="note">
              <strong>Pricing note:</strong> Artwork under $200 can be difficult to sell profitably after fulfillment. At this price, your estimated payout is ${pricingPreview.sellerNetAfterCommission.toLocaleString()} before any unusual adjustments, while the buyer-facing price includes estimated shipping and platform fees.
            </div>
          )}
          {pricingPreview && (
            <div
              className="pricing-preview"
              style={{
                marginTop: '0.75rem',
                padding: '0.85rem',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                background: '#fafafa',
                color: '#374151',
                fontSize: '0.9rem'
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>
                Pricing breakdown
              </div>
              <small style={{ display: 'block', marginBottom: '0.75rem', color: '#6b7280', lineHeight: 1.45 }}>
                Enter the amount you want to charge for the artwork. We add estimated collector shipping and a 10% buyer platform fee to the storefront price. Your estimated payout below deducts Art Emerge&apos;s 15% seller fee from your artwork price.
              </small>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span>Your artwork price</span>
                <strong>${pricingPreview.artistBasePrice.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span>Estimated collector shipping added to buyer price ({pricingPreview.shippingTier.replace(/_/g, ' ')})</span>
                <strong>${pricingPreview.estimatedShipping.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span>Buyer platform/referral fee (10%)</span>
                <strong>${pricingPreview.buyerPlatformFee.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #e5e7eb', fontSize: '1rem' }}>
                <span>Buyer-facing storefront price</span>
                <strong>${pricingPreview.buyerDisplayPrice.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                <span>Art Emerge seller fee (15%)</span>
                <strong>-${pricingPreview.sellerCommission.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '1rem' }}>
                <span>Estimated artist payout</span>
                <strong>${pricingPreview.sellerNetAfterCommission.toLocaleString()}</strong>
              </div>
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280', lineHeight: 1.45 }}>
                Final payout may vary slightly based on payment processing, refunds, taxes, or other adjustments. The post-sale shipping label tool is still used to buy the actual label once the artwork is packaged.
              </small>
              {pricingPreview.manualReview && (
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#92400e' }}>
                  This artwork may require freight handling. Treat this as a conservative estimate.
                </small>
              )}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Year Created</label>
          <input
            type="number"
            value={yearCreated}
            onChange={(e) => setYearCreated(e.target.value)}
            min="1900"
            max={new Date().getFullYear()}
            required
          />
        </div>

        <div className="form-group">
          <label>Edition Type</label>
          <select
            value={editionType}
            onChange={(e) => setEditionType(e.target.value)}
            required
          >
            <option value="original">Original</option>
            <option value="limited">Limited Edition</option>
            <option value="open">Open Edition</option>
            <option value="artist_proof">Artist Proof</option>
            <option value="reproduction">Reproduction</option>
          </select>
          <small className="form-help">
            Select the type of edition for this artwork.
          </small>
        </div>

        {editionType !== 'original' && (
          <div className="form-row">
            <div className="form-group">
              <label>Edition Number</label>
              <input
                type="number"
                value={editionNumber}
                onChange={(e) => setEditionNumber(e.target.value)}
                min="1"
                placeholder="1"
              />
              <small className="form-help">
                This specific piece's number (e.g., "1" in "1 of 100").
              </small>
            </div>

            <div className="form-group">
              <label>Edition Size</label>
              <input
                type="number"
                value={editionSize}
                onChange={(e) => setEditionSize(e.target.value)}
                min="1"
                placeholder="100"
              />
              <small className="form-help">
                Total number of pieces in this edition (e.g., "100" in "1 of 100").
              </small>
            </div>
          </div>
        )}

        {(loading || imageUploading) && (
          <div className="loading">
            {initialData ? 'Saving the new image...' : 'Uploading artwork and processing images...'}
          </div>
        )}

        {error && (
          <div className="error-message">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading || imageUploading || (images.length === 0 && !initialData)}
          className="submit-button"
        >
          {loading || imageUploading
            ? 'Uploading...'
            : initialData
              ? (images.length > 0 ? 'Save new image' : 'Update Artwork')
              : 'Upload Artwork'}
        </button>
      </form>
    );
  };

  return (
    <div className={`artwork-upload ${isModal ? 'modal' : 'page'}`}>
      {isModal ? (
        <div
          className="modal-overlay ae-dialog-scroll"
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.offsetX > e.currentTarget.clientWidth || e.offsetY > e.currentTarget.clientHeight) return;
            onClose?.();
          }}
        >
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{initialData ? 'Edit Artwork' : 'Upload Artwork'}</h2>
              <button className="close-button" onClick={onClose}>&times;</button>
            </div>
            {renderForm()}
          </div>
        </div>
      ) : (
        renderForm()
      )}
    </div>
  );
};

export default ArtworkUpload;